import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CSSProperties } from "react";
import dashboardSrc from "./beedigit-dashboard.svg";
import "./BeedigitInteractiveDashboard.css";

const VIEWBOX = { width: 1549, height: 1080 } as const;
const AUTO_FOCUS_MS = 4000;
const AMBIENT_CYCLE_MS = 8000;
const TOUCH_PAUSE_MS = 8000;
const FLOATING_PANEL_IDS = new Set<PanelId>([
  "production-summary",
  "oee-summary",
]);
const SCREEN_CROP = {
  left: 87.5,
  right: 1527.5,
  top: 62,
  bottom: 1080,
} as const;

type PanelId =
  | "machine-status"
  | "downtime-analysis"
  | "energy-consumption"
  | "production-speed"
  | "production-output"
  | "production-timeline"
  | "event-detail"
  | "production-summary"
  | "oee-summary";

type Region = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type Panel = Region & {
  id: PanelId;
  label: string;
  description: string;
  priority: number;
  hit?: Region;
  hitExtensions?: readonly Region[];
};

const PANELS: readonly Panel[] = [
  {
    id: "machine-status",
    label: "Real-time machine status",
    description: "Live OEE, uptime and operating state.",
    x: 301,
    y: 145,
    width: 640,
    height: 352,
    priority: 2,
  },
  {
    id: "downtime-analysis",
    label: "Downtime analysis",
    description: "Losses, stops and availability in real time.",
    x: 940,
    y: 139,
    width: 604,
    height: 390,
    priority: 2,
  },
  {
    id: "energy-consumption",
    label: "Energy consumption",
    description: "Live energy use and efficiency by machine or order.",
    x: 312,
    y: 503.5,
    width: 298.57,
    height: 143,
    priority: 2,
  },
  {
    id: "production-speed",
    label: "Production speed",
    description: "Actual speed compared with the operating target.",
    x: 627.57,
    y: 503.5,
    width: 298.57,
    height: 143,
    priority: 2,
  },
  {
    id: "production-output",
    label: "Production output",
    description: "Accepted and rejected output in real time.",
    x: 943.14,
    y: 503.5,
    width: 552.86,
    height: 143,
    priority: 2,
  },
  {
    id: "production-timeline",
    label: "Production timeline",
    description: "Every production state across the active shift.",
    x: 301,
    y: 653,
    width: 1206,
    height: 396,
    priority: 1,
    hit: { x: 301, y: 653, width: 1206, height: 133 },
    hitExtensions: [
      { x: 542, y: 786, width: 320, height: 263 },
      { x: 1190, y: 786, width: 317, height: 263 },
    ],
  },
  {
    id: "event-detail",
    label: "Selected event detail",
    description: "Context for every stop and operating event.",
    x: 862,
    y: 786,
    width: 328,
    height: 149,
    priority: 3,
  },
  {
    id: "production-summary",
    label: "Live production output",
    description: "Current output, speed and order progress.",
    x: 977,
    y: 0,
    width: 498,
    height: 272,
    priority: 4,
  },
  {
    id: "oee-summary",
    label: "OEE summary",
    description: "Availability, performance and quality at a glance.",
    x: 0,
    y: 706,
    width: 542,
    height: 334,
    priority: 4,
  },
] as const;
type ActivationSource = "pointer" | "keyboard" | "touch" | "auto";
type CustomProperties = CSSProperties &
  Record<`--${string}`, string | number | undefined>;

export type BeedigitInteractiveDashboardProps = {
  className?: string;
  autoFocusOnTouch?: boolean;
  showTooltips?: boolean;
};

const percent = (value: number, total: number) => `${(value / total) * 100}%`;
const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

function panelStyle(
  panel: Panel,
  region: Region = panel,
): CustomProperties {
  return {
    "--panel-x": percent(region.x, VIEWBOX.width),
    "--panel-y": percent(region.y, VIEWBOX.height),
    "--panel-width": percent(region.width, VIEWBOX.width),
    "--panel-height": percent(region.height, VIEWBOX.height),
    "--panel-priority": panel.priority,
    "--panel-origin-x": percent(panel.x + panel.width / 2, VIEWBOX.width),
    "--panel-origin-y": percent(panel.y + panel.height / 2, VIEWBOX.height),
  };
}

function panelClip(panel: Panel, isFloating: boolean) {
  const visibleLeft = isFloating
    ? panel.x
    : Math.max(panel.x, SCREEN_CROP.left);
  const visibleRight = isFloating
    ? panel.x + panel.width
    : Math.min(panel.x + panel.width, SCREEN_CROP.right);
  const visibleTop = isFloating
    ? panel.y
    : Math.max(panel.y, SCREEN_CROP.top);
  const visibleBottom = isFloating
    ? panel.y + panel.height
    : Math.min(panel.y + panel.height, SCREEN_CROP.bottom);
  const top = (visibleTop / VIEWBOX.height) * 100;
  const right =
    ((VIEWBOX.width - visibleRight) / VIEWBOX.width) * 100;
  const bottom =
    ((VIEWBOX.height - visibleBottom) / VIEWBOX.height) * 100;
  const left = (visibleLeft / VIEWBOX.width) * 100;

  return `inset(${top}% ${right}% ${bottom}% ${left}% round 5px)`;
}

function tooltipStyle(panel: Panel | null): CustomProperties {
  if (!panel) return {};

  const centerX = ((panel.x + panel.width / 2) / VIEWBOX.width) * 100;
  const top = (panel.y / VIEWBOX.height) * 100;
  const bottom = ((panel.y + panel.height) / VIEWBOX.height) * 100;
  const below = top < 24;

  return {
    "--tooltip-x": `${clamp(centerX, 18, 82)}%`,
    "--tooltip-y": `${below ? Math.min(bottom + 2, 78) : Math.max(top - 2, 22)}%`,
  };
}

export function BeedigitInteractiveDashboard({
  className = "",
  autoFocusOnTouch = true,
  showTooltips = true,
}: BeedigitInteractiveDashboardProps) {
  const [activePanelId, setActivePanelId] = useState<PanelId | null>(null);
  const [activationSource, setActivationSource] =
    useState<ActivationSource | null>(null);
  const [coarsePointer, setCoarsePointer] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const autoIndexRef = useRef(-1);
  const pauseUntilRef = useRef(0);
  const instanceId = useId().split(":").join("");
  const timelineGradientId = `beedigit-timeline-${instanceId}`;
  const ringGradientId = `beedigit-ring-${instanceId}`;

  const activePanel = useMemo(
    () => PANELS.find(({ id }) => id === activePanelId) ?? null,
    [activePanelId],
  );

  const activate = useCallback(
    (panelId: PanelId, source: ActivationSource) => {
      setActivePanelId(panelId);
      setActivationSource(source);
    },
    [],
  );

  const clear = useCallback((panelId?: PanelId) => {
    setActivePanelId((current) => {
      if (panelId && current !== panelId) return current;
      return null;
    });
    setActivationSource(null);
  }, []);

  useEffect(() => {
    const coarseQuery = window.matchMedia(
      "(hover: none) and (pointer: coarse)",
    );
    const reducedQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    const syncEnvironment = () => {
      setCoarsePointer(coarseQuery.matches);
      setReducedMotion(reducedQuery.matches);
      setPageVisible(!document.hidden);
    };

    syncEnvironment();
    coarseQuery.addEventListener("change", syncEnvironment);
    reducedQuery.addEventListener("change", syncEnvironment);
    document.addEventListener("visibilitychange", syncEnvironment);

    return () => {
      coarseQuery.removeEventListener("change", syncEnvironment);
      reducedQuery.removeEventListener("change", syncEnvironment);
      document.removeEventListener("visibilitychange", syncEnvironment);
    };
  }, []);

  useEffect(() => {
    if (
      !autoFocusOnTouch ||
      !coarsePointer ||
      reducedMotion ||
      !pageVisible
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return;

      autoIndexRef.current = (autoIndexRef.current + 1) % PANELS.length;
      activate(PANELS[autoIndexRef.current].id, "auto");
    }, AUTO_FOCUS_MS);

    return () => window.clearInterval(timer);
  }, [
    activate,
    autoFocusOnTouch,
    coarsePointer,
    pageVisible,
    reducedMotion,
  ]);

  const handleTap = (panelId: PanelId) => {
    if (coarsePointer) {
      pauseUntilRef.current = Date.now() + TOUCH_PAUSE_MS;
      activate(panelId, "touch");
      return;
    }

    activate(panelId, "pointer");
  };

  const orderedPanels = useMemo(
    () => [...PANELS].sort((a, b) => a.priority - b.priority),
    [],
  );

  return (
    <div
      className={`beedigit-hero-laptop ${className}`.trim()}
      data-active-panel={activePanelId ?? ""}
      style={{ "--ambient-cycle": `${AMBIENT_CYCLE_MS}ms` } as CustomProperties}
    >
      <div className="beedigit-hero-display">
        <span className="beedigit-hero-camera" aria-hidden="true" />
        <div
          className="beedigit-hero-screen"
          data-has-active={activePanelId ? "true" : undefined}
          data-active-source={activationSource ?? undefined}
          aria-label="Interactive BEEDIGIT manufacturing dashboard"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              (document.activeElement as HTMLElement | null)?.blur();
              clear();
            }
          }}
        >
          <div className="beedigit-hero-canvas">
            <img
              className="beedigit-hero-dashboard-base"
              src={dashboardSrc}
              alt=""
              draggable={false}
            />

            <div className="beedigit-hero-panel-layers" aria-hidden="true">
              {orderedPanels.map((panel) => {
                const isFloating = FLOATING_PANEL_IDS.has(panel.id);

                return (
                  <img
                    key={panel.id}
                    className="beedigit-hero-panel-layer"
                    src={dashboardSrc}
                    alt=""
                    draggable={false}
                    data-panel={panel.id}
                    data-active={String(activePanelId === panel.id)}
                    data-floating={String(isFloating)}
                    style={{
                      ...panelStyle(panel),
                      clipPath: panelClip(panel, isFloating),
                    }}
                  />
                );
              })}
            </div>

            <svg
              className="beedigit-hero-ambient-layer"
              viewBox="0 0 1549 1080"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id={timelineGradientId}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0" stopColor="#7f5cff" stopOpacity="0" />
                  <stop offset="0.5" stopColor="#9a83ff" stopOpacity="0.62" />
                  <stop offset="1" stopColor="#7f5cff" stopOpacity="0" />
                </linearGradient>
                <linearGradient
                  id={ringGradientId}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0" stopColor="#a88fff" />
                  <stop offset="1" stopColor="#6040e8" />
                </linearGradient>
              </defs>
              <circle
                className="beedigit-hero-ambient-ring beedigit-hero-ambient-ring-primary"
                cx="600"
                cy="326"
                r="82"
                pathLength="100"
                stroke={`url(#${ringGradientId})`}
              />
              <circle
                className="beedigit-hero-ambient-ring beedigit-hero-ambient-ring-secondary"
                cx="1134"
                cy="337"
                r="79"
                pathLength="100"
                stroke={`url(#${ringGradientId})`}
              />
              <rect
                className="beedigit-hero-ambient-summary"
                x="1000"
                y="20"
                width="440"
                height="225"
                rx="14"
              />
              <rect
                className="beedigit-hero-ambient-sweep"
                x="340"
                y="720"
                width="70"
                height="240"
                rx="24"
                fill={`url(#${timelineGradientId})`}
              />
              <circle
                className="beedigit-hero-ambient-dot beedigit-hero-ambient-dot-one"
                cx="870"
                cy="286"
                r="5"
              />
              <circle
                className="beedigit-hero-ambient-dot beedigit-hero-ambient-dot-two"
                cx="1268"
                cy="343"
                r="5"
              />
            </svg>

            <div className="beedigit-hero-hotspots">
              {orderedPanels.map((panel) => (
                <Fragment key={panel.id}>
                  <button
                    type="button"
                    className="beedigit-hero-hotspot"
                    data-panel={panel.id}
                    data-active={String(activePanelId === panel.id)}
                    aria-label={`${panel.label}. ${panel.description}`}
                    aria-pressed={activePanelId === panel.id}
                    style={panelStyle(panel, panel.hit ?? panel)}
                    onPointerEnter={() => {
                      if (!coarsePointer) activate(panel.id, "pointer");
                    }}
                    onPointerLeave={(event) => {
                      if (
                        !coarsePointer &&
                        !event.currentTarget.matches(":focus-visible")
                      ) {
                        clear(panel.id);
                      }
                    }}
                    onFocus={() => activate(panel.id, "keyboard")}
                    onBlur={() => clear(panel.id)}
                    onClick={() => handleTap(panel.id)}
                  />

                  {panel.hitExtensions?.map((region, index) => (
                    <span
                      key={`${panel.id}-extension-${index}`}
                      className="beedigit-hero-hotspot beedigit-hero-hotspot-extension"
                      data-panel={panel.id}
                      data-active={String(activePanelId === panel.id)}
                      aria-hidden="true"
                      style={panelStyle(panel, region)}
                      onPointerEnter={() => {
                        if (!coarsePointer) activate(panel.id, "pointer");
                      }}
                      onPointerLeave={() => {
                        if (!coarsePointer) clear(panel.id);
                      }}
                      onClick={() => handleTap(panel.id)}
                    />
                  ))}
                </Fragment>
              ))}
            </div>

            {showTooltips && (
              <output
                className="beedigit-hero-tooltip"
                data-visible={String(Boolean(activePanel))}
                data-side={
                  activePanel && (activePanel.y / VIEWBOX.height) * 100 < 24
                    ? "below"
                    : "above"
                }
                style={tooltipStyle(activePanel)}
                aria-live="polite"
              >
                {activePanel && (
                  <>
                    <strong>{activePanel.label}</strong>
                    <span>{activePanel.description}</span>
                  </>
                )}
              </output>
            )}
          </div>
        </div>
      </div>

      <div className="beedigit-hero-chassis" aria-hidden="true">
        <span className="beedigit-hero-lip" />
      </div>
    </div>
  );
}

export default BeedigitInteractiveDashboard;
